import React from "react";
import { Badge } from "../components";
import "./workspace-shell.css";

interface StatusBarProps {
  agentStatus: "active" | "idle" | "offline" | "connecting";
  runningTasks: number;
  sseConnected: boolean;
  activeWorkstreams?: number;
  openIssues?: number;
  pendingApprovals?: number;
}

export default function StatusBar({
  agentStatus,
  runningTasks,
  sseConnected,
  activeWorkstreams,
  openIssues,
  pendingApprovals,
}: StatusBarProps): React.ReactElement {
  const approvalCount = pendingApprovals ?? 0;
  const hasWorkspaceActivity =
    (activeWorkstreams ?? 0) > 0 || (openIssues ?? 0) > 0 || approvalCount > 0;
  const hasRunningTasks = runningTasks > 0;
  const statusColor =
    agentStatus === "active"
      ? "var(--c-success)"
      : agentStatus === "idle"
        ? "var(--c-warning)"
        : agentStatus === "connecting"
          ? "var(--c-info)"
          : "var(--c-text-faint)";

  const statusLabel =
    agentStatus === "active"
      ? "Ready to help"
      : agentStatus === "idle"
        ? "Standing by"
        : agentStatus === "connecting"
          ? "Connecting..."
          : "Offline";

  if (!hasWorkspaceActivity && !hasRunningTasks) {
    return (
      <footer className="workspace-status">
        <div className="workspace-status__segment workspace-status__segment--center">
          <span
            className={`workspace-status__dot${!sseConnected ? " workspace-status__dot--pulse" : ""}`}
            style={{ backgroundColor: sseConnected ? "var(--c-success)" : "var(--c-info)" }}
          />
          <span>
            {sseConnected
              ? "Ask your agent anything, or open a section to get started."
              : "Reconnecting to your agent..."}
          </span>
        </div>
      </footer>
    );
  }

  return (
    <footer className="workspace-status">
      {/* Left: workspace summary */}
      <div className="workspace-status__segment workspace-status__segment--left">
        {activeWorkstreams != null && activeWorkstreams > 0 && (
          <span className="workspace-status__metric">
            <span
              className="workspace-status__dot"
              style={{ backgroundColor: "var(--c-success)" }}
            />
            {activeWorkstreams} workstream{activeWorkstreams !== 1 ? "s" : ""}
          </span>
        )}
        {openIssues != null && openIssues > 0 && (
          <span className="workspace-status__metric">
            <span
              className="workspace-status__dot"
              style={{ backgroundColor: "var(--c-warning)" }}
            />
            {openIssues} open issue{openIssues !== 1 ? "s" : ""}
          </span>
        )}
        {approvalCount > 0 && (
          <span className="workspace-status__metric">
            <span
              className="workspace-status__dot"
              style={{ backgroundColor: "var(--c-info)" }}
            />
            {approvalCount} pending approval{approvalCount !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Center: agent status */}
      <div className="workspace-status__segment workspace-status__segment--center">
        <span
          className={`workspace-status__dot${agentStatus === "connecting" ? " workspace-status__dot--pulse" : ""}`}
          style={{ backgroundColor: statusColor }}
        />
        <span>{statusLabel}</span>
      </div>

      {/* Right: tasks + connection */}
      <div className="workspace-status__segment workspace-status__segment--right">
        {runningTasks > 0 && (
          <span className="workspace-status__metric">
            {runningTasks} task{runningTasks !== 1 ? "s" : ""} running
          </span>
        )}
        {!sseConnected && (
          <Badge color="red" dot>
            Disconnected
          </Badge>
        )}
      </div>
    </footer>
  );
}
